import TextField from 'lib/ui/TextField'
import {useConfig} from 'organization/Event/Configurable'
import Config from 'organization/Event/Configurable/Config'
import SettingsPanel from 'organization/Event/DashboardConfig/ComponentConfigPanel/SettingsPanel'
import StylingPanel from 'organization/Event/DashboardConfig/ComponentConfigPanel/StylingPanel'
import React, {useState} from 'react'
import TypographySection from 'Event/Dashboard/components/NavButton/NavButtonConfig/BaseConfig/Styling/TypographySection'
import BackgroundSection from 'Event/Dashboard/components/NavButton/NavButtonConfig/BaseConfig/Styling/BackgroundSection'
import SizeSection from 'Event/Dashboard/components/NavButton/NavButtonConfig/BaseConfig/Styling/SizeSection'
import BorderSection from 'Event/Dashboard/components/NavButton/NavButtonConfig/BaseConfig/Styling/BorderSection'
import {REMOVE} from 'Event/TemplateUpdateProvider'
import VisibilitySetting from 'organization/Marketplace/config/BlockConfig/VisibilitySettings'
import {useConfigurableSection} from 'organization/Marketplace/config/ConfigurableSection'
import MoveSectionButtons from 'organization/Marketplace/config/BlockConfig/MoveSectionButtons'
import {useSection} from 'Event/Marketplace/Section'
import {UpsellDeclineButtonBlock} from 'Event/Marketplace/Block/UpsellDeclineButton'
import ButtonStyleClipboardActions, {
  ButtonStyleClipboard,
} from 'lib/ui/Button/ButtonStyleClipboardActions'

interface UpselLDeclineButtonConfigProps extends UpsellDeclineButtonBlock {
  id: string
  sectionId: string
  onMoveSectionUp?: () => void
  onMoveSectionDown?: () => void
}

export default function UpsellPurchaseButtonConfig(
  props: UpselLDeclineButtonConfigProps,
) {
  const {id, onMoveSectionDown, onMoveSectionUp} = props

  const {update} = useConfigurableSection()
  const {calculateVisibility} = useSection()
  const {
    form: {register, control, watch, setValue},
  } = useConfig()
  const [visibility, setVisibility] = useState(props.visibility)

  const pasteButtonStyle = (style: ButtonStyleClipboard) => {
    const set = (name: string, value: unknown) => {
      if (value === undefined) {
        return
      }

      setValue(name, value, {shouldDirty: true})
    }

    set('disableHover', style.disableHover)

    // The hover color inputs only mount on the next render when the pasted
    // disableHover reveals them, so the rest is set a tick later.
    setTimeout(() => {
      set('font', style.font)
      set('fontSize', style.fontSize)
      set('textColor', style.textColor)
      set('hoverTextColor', style.hoverTextColor)
      set('backgroundColor', style.backgroundColor)
      set('hoverBackgroundColor', style.hoverBackgroundColor)
      set('borderColor', style.borderColor)
      set('hoverBorderColor', style.hoverBorderColor)
      set('borderWidth', style.borderWidth)
      set('borderRadius', style.borderRadius)
      set('height', style.height)
      set('width', style.widthPercent)
    }, 0)
  }

  const handleRemove = () => {
    update({
      blocks: {
        [id]: REMOVE,
      },
    })
    calculateVisibility()
  }

  const save = (data: any) => {
    data.visibility = visibility
    update({
      blocks: {
        [id]: data,
      },
    })
    calculateVisibility()
  }

  return (
    <Config title="Upsell Decline Button" onSave={save} onRemove={handleRemove}>
      <SettingsPanel>
        <TextField
          name={`text`}
          defaultValue={props.text || ''}
          label="Text Line 1"
          inputProps={{
            ref: register,
            'aria-label': 'button text input',
          }}
        />
        <VisibilitySetting
          visibility={visibility}
          setVisibility={setVisibility}
        />
        <MoveSectionButtons
          onMoveUp={onMoveSectionUp}
          onMoveDown={onMoveSectionDown}
        />
      </SettingsPanel>
      <StylingPanel>
        <ButtonStyleClipboardActions
          values={{
            font: watch('font', props.font),
            fontSize: watch('fontSize', props.fontSize),
            textColor: watch('textColor', props.textColor),
            hoverTextColor: watch('hoverTextColor', props.hoverTextColor),
            backgroundColor: watch('backgroundColor', props.backgroundColor),
            hoverBackgroundColor: watch(
              'hoverBackgroundColor',
              props.hoverBackgroundColor,
            ),
            disableHover: watch('disableHover'),
            borderColor: watch('borderColor', props.borderColor),
            hoverBorderColor: watch('hoverBorderColor', props.hoverBorderColor),
            borderWidth: watch('borderWidth', props.borderWidth),
            borderRadius: watch('borderRadius', props.borderRadius),
            height: watch('height', props.height),
            widthPercent: watch('width', props.width),
          }}
          onPaste={pasteButtonStyle}
        />
        <TypographySection
          button={props}
          control={control}
          storageKey={`blocks-${id}-styling.typography`}
          label="Typography"
        />
        <BackgroundSection
          button={props}
          control={control}
          watch={watch}
          storageKey={`blocks-${id}-styling.background`}
        />
        <SizeSection
          button={props}
          control={control}
          storageKey={`blocks-${id}-styling.size`}
          sizeUnit="percent"
          disableNewLine
        />
        <BorderSection
          button={props}
          control={control}
          storageKey={`blocks-${id}-styling.border`}
        />
      </StylingPanel>
    </Config>
  )
}
